"use client";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const Hero = () => {
  const scrollToSection = (sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="pt-24 pb-16">
      <div className="container mx-auto px-6">
        {/* Main Heading - Full Width */}
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold text-foreground leading-tight max-w-4xl mx-auto tracking-tight text-balance">
            Building the New Real Estate Ownership Economy
          </h1>
        </div>

        {/* Three Columns */}
        <div className="grid lg:grid-cols-3 gap-12 items-stretch">
          {/* Left Column - Home Buyers */}
          <div className="flex flex-col space-y-6 h-full">
            <Card className="p-8 bg-panel border-border flex-1">
              <div className="text-3xl mb-4">🏠</div>
              <h3 className="font-semibold text-foreground mb-3 text-lg">For Home Buyers</h3>
              <p className="text-muted-foreground">
                Buy without the traditional 20% down payment, no additional debt, and no PMI.
              </p>
            </Card>
            <Button
              className="mt-auto bg-accent hover:bg-accent-hover text-accent-foreground w-full"
              onClick={() => scrollToSection("waitlist")}
            >
              Join Waitlist (Home Buyers)
            </Button>
          </div>

          {/* Middle Column - Investors */}
          <div className="flex flex-col space-y-6 h-full">
            <Card className="p-8 bg-panel border-border flex-1">
              <div className="text-3xl mb-4">📈</div>
              <h3 className="font-semibold text-foreground mb-3 text-lg">For Investors</h3>
              <p className="text-muted-foreground">
                Participate in a new asset class built on real estate.
              </p>
            </Card>
            <Button
              className="mt-auto bg-investor hover:bg-investor-hover text-investor-foreground w-full"
              onClick={() => scrollToSection("investors")}
            >
              Request Investor Brief
            </Button>
          </div>

          {/* Right Column - What to Expect */}
          <div className="flex flex-col space-y-6 h-full">
            <Card className="p-8 bg-panel border-border h-full flex flex-col">
              <div className="space-y-4 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-foreground">What to Expect</h3>
                  <Badge variant="secondary" className="text-xs">
                    Teaser • No confidential details
                  </Badge>
                </div>

                <p className="text-muted-foreground">
                  High-level overview now. Details shared selectively after you submit the form below.
                </p>

                <ul className="space-y-3 text-sm text-muted-foreground flex-1">
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    No fees or mechanics disclosed on this page
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    Home buyer and investor paths handled separately
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    We respond to qualified requests only
                  </li>
                </ul>
              </div>
            </Card>
          </div>
        </div>

        {/* Three Mini Cards - Full Width */}
        <div className="grid md:grid-cols-3 gap-8 pt-20 max-w-4xl mx-auto">
          <div className="text-center space-y-3">
            <h4 className="font-medium text-foreground text-xl mb-2">Problem-first</h4>
            <p className="text-muted-foreground">We start with the buyer barrier: down payments.</p>
          </div>
          <div className="text-center space-y-3">
            <h4 className="font-medium text-foreground text-xl mb-2">Platform mindset</h4>
            <p className="text-muted-foreground">Ecosystem-friendly from day one.</p>
          </div>
          <div className="text-center space-y-3">
            <h4 className="font-medium text-foreground text-xl mb-2">Institutional path</h4>
            <p className="text-muted-foreground">Designed for scale & credibility.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
